import { makeStyles } from '@material-ui/styles'
import type { GridCellProps } from 'pf9-ui-components/built/elements/grid/hooks/useGridRows'
import { pick } from 'ramda'
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { listTablePrefs } from 'app/constants'
import { usagePercentSort } from 'app/plugins/openstack/components/infrastructure/hosts/helpers'
import { IVmDetailsPageTabs } from 'app/plugins/openstack/components/vms/vm-details/model'
import type InferActionParams from 'core/actions/InferActionParams'
import CreateButton from 'core/components/buttons/CreateButton'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { DateAndTime } from 'core/components/listTable/cells/DateCell'
import SimpleLink from 'core/components/SimpleLink'
import ListContainer from 'core/containers/ListContainer'
import Badge from 'core/elements/badge'
import { createGridStatusCell, type StatusCellModel } from 'core/elements/grid/cells/GridStatusCell'
import type { GridViewColumn } from 'core/elements/grid/Grid'
import type { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import type { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import type { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip/Tooltip'
import useClusterChangeReload from 'core/hooks/useClusterChangeReload'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useScopedPreferences from 'core/session/useScopedPreferences'
import type Theme from 'core/themes/model'
import { isReadonlyUser } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { isNilOrEmpty } from 'utils/fp'
import UsageProgress from '../capacity-and-health/UsageProgress'
import GPUDetailsModal from '../clusters-dashboard/GPUDetailsModal'
import { kaapiClustersOverviewSelector } from '../kaapi/clusters/selectors'
import type { IMachineDeploymentSelector } from '../kaapi/machine-deployment/model'
import { listKaapiMachines } from '../kaapi/machines/actions'
import MachineStatusCell from '../kaapi/machines/MachineStatusCell'
import { kaapiMachinesForClusterSelector } from '../kaapi/machines/selectors'
import { listKaapiOpenStackServers } from '../kaapi/openstack-servers/actions'
import { kaapiOpenStackServersSelector } from '../kaapi/openstack-servers/selectors'
import { listByoHosts } from '../nodes/byoh/actions'
import AddPhysicalNodes from './AddPhysicalNodes'
import DeleteWorkerNodesDialog from './DeleteWorkerNodesDialog'
import EditMasterNodeModal from './EditMasterNodeModal'
import { buildWorkerNodeFilters } from './workerNodeFilters'

type ActionParams = InferActionParams<typeof listKaapiMachines>
type Params = ActionParams & {}

const defaultParams: Params & SortingState = {
  orderBy: null,
  orderDirection: 'asc',
}

const usePrefParams = createUsePrefParamsHook('KaapiMachines', listTablePrefs)

const useWorkerNodeCellComponentStyles = makeStyles((theme: Theme) => ({
  disabled: {
    cursor: 'not-allowed',
  },
  warning: {
    background: theme.palette.graphYellow[100],
    color: theme.palette.graphYellow[500],
  },
  nameContainer: {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    alignItems: 'center',
  },
}))

export type NodeGroupSelector = IMachineDeploymentSelector

const Phase = {
  Provisioning: 'Provisioning',
  Provisioned: 'Provisioned',
  Running: 'Running',
  Failed: 'Failed',
  ScalingUp: 'ScalingUp',
  ScalingDown: 'ScalingDown',
  Deleting: 'Deleting',
  Unknown: 'Unknown',
  Ready: 'Ready',
  Pending: 'Pending',
}

const MACHINE_FAILURE_PHASES = [Phase.Failed, Phase.Deleting, Phase.Unknown]

// OpenStack Machine States
export const getMachineStatus = (phase): StatusCellModel => {
  switch (phase) {
    case Phase.Running:
    case Phase.Provisioned:
    case Phase.Ready:
      return { variant: 'success', label: phase }
    case Phase.Provisioning:
    case Phase.ScalingUp:
    case Phase.ScalingDown:
    case Phase.Pending:
      return { variant: 'warning', label: phase }
    case Phase.Failed:
    case Phase.Deleting:
      return { variant: 'error', label: phase }
    default:
      return { variant: 'unknown', label: phase || Phase.Unknown }
  }
}

// K8s Node States
export const getK8sNodeStatus = (phase): StatusCellModel => {
  switch (phase) {
    case 'Ready':
      return { variant: 'success', label: phase }
    case 'MemoryPressure':
    case 'DiskPressure':
    case 'PIDPressure':
      return { variant: 'warning', label: phase }
    case 'NetworkUnavailable':
    case 'NotReady':
      return { variant: 'error', label: phase }
    default:
      return { variant: 'unknown', label: phase || Phase.Unknown }
  }
}

const cellFormatFn = (value) => (isNilOrEmpty(value) ? '-' : value)

// Extract UUID from providerId Eg. openstack:///283d5beb-4f02-4fd5-ad4f-ebc7cf472060
function extractUUID(providerId) {
  const match = providerId?.match(/[0-9a-fA-F-]{36}/)
  return match ? match[0] : null
}

export function WorkerNodeCellComponent({ value, item }: GridCellProps<any>) {
  const classes = useWorkerNodeCellComponentStyles()

  const vmID = extractUUID(item?.spec?.providerID)
  const linkToVM = routeManager.routes.openstack.vmDetails.path({
    id: vmID,
    tab: IVmDetailsPageTabs.Overview,
  })
  const isMachineFailure = item?.isBYOMachine || MACHINE_FAILURE_PHASES.includes(item?.phase)

  return (
    <div className={classes.nameContainer}>
      {isMachineFailure ? (
        <Text variant="body2">{value as string}</Text>
      ) : (
        <Tooltip message={!vmID ? 'VM not provisioned yet' : ''}>
          <SimpleLink className={!vmID && classes.disabled} src={vmID ? linkToVM : null}>
            {value as string}
          </SimpleLink>
        </Tooltip>
      )}
      {item?.unschedulable && (
        <Tooltip message="Scheduling disabled">
          <FontAwesomeIcon className={classes.warning}>triangle-exclamation</FontAwesomeIcon>
        </Tooltip>
      )}
    </div>
  )
}

export const GPUMemoryCellComponent = ({ value }) => (
  <Text variant="body2">{`${(Number(value) / 1028).toFixed(2)} GB` || '-'}</Text>
)

export const workerNodeColumns = (isBYOHCluster: boolean): GridViewColumn<any>[] => [
  {
    key: 'name',
    label: 'Name',
    CellComponent: WorkerNodeCellComponent,
  },
  {
    key: 'gpu.enabled',
    label: 'GPU Enabled',
    CellComponent: ({ value }) => <Text variant="body2">{value ? 'Yes' : 'No'}</Text>,
  },
  {
    key: 'gpu.model',
    label: 'GPU Model',
    CellComponent: ({ value }) => <Text variant="body2">{value || '-'}</Text>,
    display: false,
  },
  {
    key: 'gpu.partitioningStrategy',
    label: 'GPU Strategy',
    CellComponent: ({ value, item }) => <GPUDetailsModal item={item} value={value} />,
    display: false,
  },
  {
    key: 'gpu.details',
    label: 'GPU Details',
    CellComponent: ({ value }) => <Text variant="body2">{value || '-'}</Text>,
    display: false,
  },
  {
    key: 'gpu.count',
    label: 'GPU Count',
    sortType: 'number',
    CellComponent: ({ value }) => <Text variant="body2">{value || '-'}</Text>,
    display: false,
  },
  {
    key: 'gpu.replicas',
    label: 'GPU Replicas',
    sortType: 'number',
    CellComponent: ({ value }) => <Text variant="body2">{value || '-'}</Text>,
    display: false,
  },
  {
    key: 'gpu.memory',
    label: 'GPU Memory',
    sortType: 'number',
    CellComponent: GPUMemoryCellComponent,
    display: false,
  },
  {
    key: 'nodeType',
    label: 'Node Type',
    disableSorting: true,
    CellComponent: (props) => {
      if (!props?.value) return <Text variant="body2">-</Text>
      return <Badge text={props.value} variant="primary" hideTooltip />
    },
  },
  {
    key: 'k8sNodeStatusType',
    label: 'K8s Node status',
    CellComponent: createGridStatusCell({
      dataFn: (node: NodeGroupSelector): StatusCellModel => {
        return getK8sNodeStatus(node)
      },
    }),
  },
  {
    key: 'phase',
    label: 'Machine Status',
    CellComponent: MachineStatusCell,
    memoizeCell: false,
  },
  {
    key: 'machineDeployment.metadata.name',
    label: 'Node Group',
    display: false,
  },
  ...[
    isBYOHCluster
      ? {
          key: 'physicalNode',
          label: 'Node',
        }
      : {
          key: 'machineFlavor',
          label: 'Flavor',
        },
  ],
  {
    key: 'spec.version',
    label: 'K8s Version',
    formatFn: (value) => (isNilOrEmpty(value) ? '-' : value),
  },
  {
    key: 'metadata.creationTimestamp',
    label: 'Created',
    CellComponent: DateAndTime,
  },
  {
    key: 'cpuUsage',
    label: 'CPU Usage (cores)',
    CellComponent: UsageProgress,
    sortFn: usagePercentSort,
  },
  {
    key: 'memoryUsage',
    label: 'Memory Usage (GiB)',
    CellComponent: UsageProgress,
    sortFn: usagePercentSort,
  },
]

export default function ListWorkerNodes({ setIsEditMasterNodesModalOpen }) {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const [showEditMasterModal, setShowEditMasterModal] = useState(false)
  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences('k8sPluginGlobalParams')

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedWorkerNode, setSelectedWorkerNode] = useState(null)
  const [showAddPhysicalNodesModal, setShowAddPhysicalNodesModal] = useState(false)

  const allClusters = useSelector(kaapiClustersOverviewSelector)
  const cluster = allClusters?.find((c) => c?.metadata?.name === k8sPluginGlobalPerfs?.cluster)

  // Fetch KCP and Openstack Machine Templates for the cluster

  const isClusterCustomControlPlane = useMemo(() => {
    return cluster?.controlPlaneType === 'Colocated'
  }, [cluster])

  const machinesSelector = kaapiMachinesForClusterSelector({
    clusterName: k8sPluginGlobalPerfs?.cluster,
  })
  const machines = useSelector(machinesSelector) || []

  // Use machines loaded by parent (ClusterDetailTabs)
  // Load immediately if data empty; defer if already cached
  const { loading: loadingKaapiMachines, reload: reloadKaapiMachines } = useListAction(
    listKaapiMachines,
    { loadOnDemand: machines.length > 0 },
  )

  const { loading: loadingKaapiOpenStackServers, reload: reloadKaapiOpenStackServers } =
    useListAction(listKaapiOpenStackServers)
  const kaapiOpenStackServers = useSelector(kaapiOpenStackServersSelector)

  // ByoHosts carry the physical host's own registered name, used as a fallback
  // for the Node column while a machine is still provisioning (nodeRef not yet
  // set). This page can be reached directly without visiting the dashboard, so
  // it must fetch this itself rather than relying on ClusterDetails having done it.
  const { reload: reloadByoHosts } = useListAction(listByoHosts, {
    preRequisites: !!cluster?.isBYOHCluster,
  })

  const reloadMachines = useCallback(async () => {
    await Promise.all([
      reloadKaapiMachines(true, true),
      reloadKaapiOpenStackServers(true, true),
      reloadByoHosts(true, true),
    ])
  }, [reloadKaapiMachines, reloadKaapiOpenStackServers, reloadByoHosts])

  // Refresh the node list only when cluster ACTUALLY changes (not on first mount)
  useClusterChangeReload(k8sPluginGlobalPerfs.cluster, reloadMachines)

  const filterMachines = useMemo(() => {
    return machines?.map((machine) => {
      const openStackServer = kaapiOpenStackServers?.find(
        (server) => server?.metadata?.name === machine?.name,
      )
      return {
        ...machine,
        machineFlavor:
          machine?.machineDeployment?.openStackMachineTemplate?.spec?.template?.spec?.flavor ||
          openStackServer?.spec?.flavor ||
          '-',
      }
    })
  }, [machines, kaapiOpenStackServers])

  // Check if this is the last running node in the cluster
  const isLastRunningNode: boolean = useMemo(
    () => machines?.length === 1 && machines[0]?.status.phase === 'Running',
    [machines],
  )

  const isBYOHCluster = cluster?.isBYOHCluster

  const { masterFilterMachines, workerFilterMachines } = useMemo(() => {
    const master = []
    const worker = []
    filterMachines.forEach((m) => {
      m.nodeType === 'Control Plane' ? master.push(m) : worker.push(m)
    })
    return { masterFilterMachines: master, workerFilterMachines: worker }
  }, [filterMachines])

  const showUpdateMasterButton = !isReadonlyUser() && !!masterFilterMachines?.length

  const dropdownFilters = useMemo(
    () => buildWorkerNodeFilters(filterMachines, isBYOHCluster),
    [filterMachines, isBYOHCluster],
  )

  const batchActions: GridBatchActionSpec<any>[] = [
    {
      label: 'Delete',
      icon: 'trash-alt',
      handleAction: (workerNode) => {
        setSelectedWorkerNode(workerNode)
        setShowDeleteDialog(true)
      },
      cond: ([workerNode]) => workerNode?.nodeType !== 'Control Plane',
      disabledTooltip: 'Control plane nodes cannot be deleted',
    },
  ]

  const rowMenuItems: GridRowMenuItemSpec<any>[] = [
    {
      label: 'Delete',
      icon: 'trash-alt',
      handleClick: (workerNode) => {
        setSelectedWorkerNode([workerNode])
        setShowDeleteDialog(true)
      },
      refreshAfterSuccess: true,
      hideIfDisabled: true,
      cond: (node) => node.nodeType !== 'Control Plane',
    },
  ]

  const showBYOAddNodesButton = !isReadonlyUser() && cluster?.isBYOHCluster

  return (
    <>
      {showDeleteDialog && (
        <DeleteWorkerNodesDialog
          data-testid="delete-worker-nodes-dialog"
          rows={selectedWorkerNode}
          onClose={() => setShowDeleteDialog(false)}
          isLastRunningNode={isLastRunningNode}
          reloadKaapiMachines={reloadMachines}
          isBYOHCluster={cluster?.isBYOHCluster}
        />
      )}
      {showAddPhysicalNodesModal && (
        <AddPhysicalNodes
          onClose={() => setShowAddPhysicalNodesModal(false)}
          isClusterCustomControlPlane={isClusterCustomControlPlane}
          clusterName={cluster?.metadata?.name}
          masterReplicas={Number(cluster?.spec?.topology?.controlPlane?.replicas)}
        />
      )}
      {showEditMasterModal && masterFilterMachines?.length && (
        <EditMasterNodeModal
          onClose={() => {
            setShowEditMasterModal(false)
            setIsEditMasterNodesModalOpen(false)
          }}
          rows={masterFilterMachines}
          clusterName={cluster?.metadata?.name}
          data-testid="edit-master-node-modal"
        />
      )}

      {isClusterCustomControlPlane && (
        <ListContainer<any, any>
          data-testid="control-plane-nodes-list-container"
          searchTargets={['name', 'gpuModel', 'phase']}
          uniqueIdentifier="uid"
          loading={loadingKaapiMachines || loadingKaapiOpenStackServers}
          loadingMessage="Loading..."
          data={masterFilterMachines}
          columns={workerNodeColumns(cluster?.isBYOHCluster)}
          getParamsUpdater={getParamsUpdater}
          label="Control Plane Nodes"
          onRefresh={reloadMachines}
          showItemsCountInLabel
          showBreadcrumbs={false}
          batchActions={[]}
          rowMenuItems={[]}
          showRowMenuForSingleRowActions={false}
          dropdownFilters={dropdownFilters}
          disableRowSelection
          {...pick(listTablePrefs, params)}
          extraToolbarContent={
            showUpdateMasterButton && !showBYOAddNodesButton ? (
              <CreateButton
                icon="edit"
                onClick={() => {
                  setShowEditMasterModal(true)
                  setIsEditMasterNodesModalOpen(true)
                }}
                data-testid="update-master-nodes-button"
                disabled={isReadonlyUser() || !masterFilterMachines?.length}
              >
                Edit Control Plane Nodes
              </CreateButton>
            ) : null
          }
        />
      )}

      <ListContainer<any, any>
        data-testid="worker-nodes-list-container"
        searchTargets={['name', 'gpuModel', 'phase']}
        uniqueIdentifier="uid"
        loading={loadingKaapiMachines || loadingKaapiOpenStackServers}
        loadingMessage="Loading..."
        multiSelection
        data={workerFilterMachines}
        columns={workerNodeColumns(cluster?.isBYOHCluster)}
        getParamsUpdater={getParamsUpdater}
        label="Worker Nodes"
        onRefresh={reloadMachines}
        showItemsCountInLabel
        showBreadcrumbs={false}
        batchActions={batchActions}
        rowMenuItems={rowMenuItems}
        showRowMenuForSingleRowActions
        dropdownFilters={dropdownFilters}
        {...pick(listTablePrefs, params)}
        extraToolbarContent={
          showBYOAddNodesButton ? (
            <CreateButton
              icon="add"
              onClick={() => setShowAddPhysicalNodesModal(true)}
              data-testid="add-node-group-button"
            >
              Add Physical Nodes
            </CreateButton>
          ) : null
        }
      />
    </>
  )
}
